/**
 * Native Bridge — AuthChannel messaging for Android / iOS WebViews.
 *
 * Execution paths:
 * - Android WebView:  window.AuthChannel.postMessage(JSON string)
 * - iOS WKWebView:    window.webkit.messageHandlers.AuthChannel.postMessage(object)
 * - Web browser:      no bridge present → no-op (returns false, never throws)
 *
 * Native shells must inject/register a handler named "AuthChannel".
 * Calling these helpers in a normal browser is safe and does not affect web flow.
 */

/**
 * Detect Android WebView AuthChannel (JavaScriptInterface / addJavascriptInterface).
 * @returns {boolean}
 */
function hasAndroidAuthChannel() {
    return !!(
        typeof window !== 'undefined' &&
        window.AuthChannel &&
        typeof window.AuthChannel.postMessage === 'function'
    );
}

/**
 * Detect iOS WKWebView AuthChannel (WKScriptMessageHandler).
 * @returns {boolean}
 */
function hasIosAuthChannel() {
    return !!(
        typeof window !== 'undefined' &&
        window.webkit &&
        window.webkit.messageHandlers &&
        window.webkit.messageHandlers.AuthChannel &&
        typeof window.webkit.messageHandlers.AuthChannel.postMessage === 'function'
    );
}

/**
 * Post a payload to the native app when running inside a WebView.
 * Web browsers: returns false and does nothing.
 *
 * @param {object} data - Message payload (plain object)
 * @returns {boolean} true if a native channel accepted the message
 */
export function postToNative(data) {
    if (!data || typeof data !== 'object') {
        return false;
    }

    // --- Android WebView ---
    // Android expects a JSON string via AuthChannel.postMessage.
    if (hasAndroidAuthChannel()) {
        try {
            window.AuthChannel.postMessage(JSON.stringify(data));
            return true;
        } catch (error) {
            console.error('[nativeBridge] Android AuthChannel.postMessage failed', error);
            return false;
        }
    }

    // --- iOS WKWebView ---
    // iOS expects a JavaScript object (not a JSON string).
    if (hasIosAuthChannel()) {
        try {
            window.webkit.messageHandlers.AuthChannel.postMessage(data);
            return true;
        } catch (error) {
            console.error('[nativeBridge] iOS AuthChannel.postMessage failed', error);
            return false;
        }
    }

    // --- Web browser (or unknown shell) ---
    // No AuthChannel bridge: silent no-op so portal login/logout stay unchanged.
    return false;
}

/**
 * Notify the native shell that the user logged in successfully.
 * Call after auth succeeds and user identity is known.
 *
 * @param {string} userName
 * @returns {boolean}
 */
export function notifyNativeLogin(userName) {
    return postToNative({
        action: 'login',
        userName: userName == null ? '' : String(userName)
    });
}

/**
 * Notify the native shell that the user is logging out.
 * Call before clearing localStorage / sessionStorage or redirecting to login.
 *
 * @returns {boolean}
 */
export function notifyNativeLogout() {
    return postToNative({
        action: 'logout'
    });
}

/** Apple's subscription management page. StoreKit cannot cancel IAP from this web app. */
export const APPLE_SUBSCRIPTIONS_URL = 'https://apps.apple.com/account/subscriptions';

/**
 * Ask the native shell to open Apple subscription management.
 * Falls back to the same Apple page when no WebView bridge is present.
 * @returns {boolean} true when a native channel accepted the message
 */
export function openAppleSubscriptionManagement() {
    const openedByApp = postToNative({
        action: 'openAppleSubscriptions',
        url: APPLE_SUBSCRIPTIONS_URL
    });
    // Always open Apple's page. The native message is optional for a StoreKit handoff.
    if (typeof window !== 'undefined') {
        window.location.href = APPLE_SUBSCRIPTIONS_URL;
    }
    return openedByApp;
}

export default {
    postToNative,
    notifyNativeLogin,
    notifyNativeLogout,
    openAppleSubscriptionManagement,
    APPLE_SUBSCRIPTIONS_URL
};
